export type DncChannel = "EMAIL" | "SMS" | "TELEPHONE" | "WHATSAPP";
export type SuppressionScope = "ASSOCIATE" | "KWSA";
export type DncSourceCategory = "NONE" | "INTERNAL" | "NATIONAL_REGISTRY" | "BOTH";
export type DncDecision = "CLEAR" | "DO_NOT_CONTACT" | "REVIEW_REQUIRED";

export function associateLabel(name: string | null | undefined, kwuid: string | null | undefined): string {
  const cleanName = name?.trim();
  const cleanKwuid = kwuid?.trim();
  if (cleanName && cleanKwuid) return `${cleanName} · KWUID ${cleanKwuid}`;
  if (cleanName) return cleanName;
  if (cleanKwuid) return `KWUID ${cleanKwuid}`;
  return "Not attributed";
}

export type DncChannelResult = {
  status: "CLEAR" | "SUPPRESSED" | "REVIEW_REQUIRED";
  source: DncSourceCategory;
  recordedAt: string | null;
  scope?: SuppressionScope | null;
};

export type DncEligibility = {
  status: "CLEAR" | "SUPPRESSED_INTERNAL" | "SUPPRESSED_NATIONAL" | "SUPPRESSED_BOTH" | "REVIEW_REQUIRED";
  source: DncSourceCategory;
  suppressionScope?: SuppressionScope | null;
  channels: Partial<Record<DncChannel, DncChannelResult>>;
  /** Present only for Regional Compliance administrators. */
  associateScopedSuppressions?: Array<{ channel: string; targetAgentKwuid: string; recordedAt: string }>;
};

export type DncSummary = {
  available: boolean;
  internalSuppressions?: number;
  kwsaWideSuppressions?: number;
  associateSpecificSuppressions?: number;
  nationalRegistrySuppressions?: number;
  nationalRegistryConnected?: boolean;
  reviewRequired?: number;
};

export type DncRecord = {
  record_id: string;
  recorded_at: string;
  consumer_first_name: string | null;
  consumer_last_name: string | null;
  identifiers: string[] | null;
  normalized_identifiers: string[] | null;
  channels: string[] | null;
  action: string;
  source_category: "INTERNAL" | "NATIONAL_REGISTRY";
  suppression_scope: SuppressionScope;
  target_agent_kwuid: string | null;
  source_type: string | null;
  source_agent_kwuid: string | null;
  source_agent_name?: string | null;
  source_market_centre_id: string | null;
  source_market_centre_name: string | null;
  reason: string | null;
};

export type DncAuditEvent = {
  id: string;
  consumer_first_name: string | null;
  consumer_last_name: string | null;
  identifier_type: string | null;
  raw_identifier: string | null;
  normalized_identifier: string | null;
  dnc_type: string;
  action: string;
  reason: string | null;
  channel: string | null;
  recorded_at: string;
  source_type: string | null;
  source_agent_kwuid: string | null;
  source_agent_name?: string | null;
  source_market_centre_id: string | null;
  source_reference: string | null;
  suppression_scope: SuppressionScope | null;
  target_agent_kwuid: string | null;
  captured_by_associate_id: string | null;
  captured_by_user_email: string | null;
};

export const dncChannels: Array<{ value: DncChannel; label: string }> = [
  { value: "EMAIL", label: "Email" },
  { value: "SMS", label: "SMS" },
  { value: "TELEPHONE", label: "Telephone" },
  { value: "WHATSAPP", label: "WhatsApp" },
];

export const decisionLabels: Record<DncDecision, string> = {
  CLEAR: "CLEAR",
  DO_NOT_CONTACT: "DO NOT CONTACT",
  REVIEW_REQUIRED: "REVIEW REQUIRED",
};

export const decisionTone: Record<DncDecision, "good" | "danger" | "warning"> = {
  CLEAR: "good",
  DO_NOT_CONTACT: "danger",
  REVIEW_REQUIRED: "warning",
};

export const decisionGuidance: Record<DncDecision, string> = {
  CLEAR: "No applicable suppression was found. Proceed in line with normal marketing practice.",
  DO_NOT_CONTACT: "Do not send direct marketing through the channels marked below.",
  REVIEW_REQUIRED: "The status could not be confirmed. Do not send direct marketing until it has been reviewed.",
};

export const scopeLabels: Record<SuppressionScope, string> = {
  KWSA: "KWSA-wide preference",
  ASSOCIATE: "Associate-specific preference",
};

export const sourceCategoryLabels: Record<DncSourceCategory, string> = {
  NONE: "No suppression found",
  INTERNAL: "Internal",
  NATIONAL_REGISTRY: "National Registry",
  BOTH: "Internal + National Registry",
};

const sourceTypeLabels: Record<string, string> = {
  AGENT_WEBSITE: "Agent website",
  AGENT_VCARD: "Agent VCard",
  REGION_WEBSITE: "KWhomes",
  MARKET_CENTRE_WEBSITE: "Market Centre website",
  EMAIL_FOOTER: "Email footer",
  CAMPAIGN: "Campaign",
  QR_CODE: "QR code",
  MANUAL_ADMIN: "Recorded by administrator",
  OTHER: "Other",
};

export function dncUiEnabled(): boolean {
  return import.meta.env.VITE_DNC_COMPLIANCE_ENABLED === "true";
}

/** Mirrors the backend rule: full DNC records are limited to Regional Admins in Regional scope. */
export function canViewDncRecords(isRegionalAdmin: boolean, activeContextId: string | null | undefined): boolean {
  return isRegionalAdmin && Boolean(activeContextId?.startsWith("regional_admin"));
}

export function channelDecision(channel: DncChannelResult | undefined): DncDecision {
  if (!channel || channel.status === "REVIEW_REQUIRED") return "REVIEW_REQUIRED";
  return channel.status === "SUPPRESSED" ? "DO_NOT_CONTACT" : "CLEAR";
}

export function overallDecision(result: DncEligibility): DncDecision {
  if (result.status === "CLEAR") return "CLEAR";
  if (result.status === "REVIEW_REQUIRED") return "REVIEW_REQUIRED";
  return "DO_NOT_CONTACT";
}

/** Plain-language basis for the decision, e.g. "Associate-specific preference" or "KWSA-wide preference + National Registry". */
export function suppressionBasis(result: DncEligibility): string | null {
  const parts: string[] = [];
  const suppressed = dncChannels.map(({ value }) => result.channels[value]).filter((channel) => channel?.status === "SUPPRESSED");
  const internal = suppressed.filter((channel) => channel?.source === "INTERNAL" || channel?.source === "BOTH");
  if (internal.some((channel) => channel?.scope !== "ASSOCIATE")) parts.push(scopeLabels.KWSA);
  if (internal.some((channel) => channel?.scope === "ASSOCIATE")) parts.push(scopeLabels.ASSOCIATE);
  if (suppressed.some((channel) => channel?.source === "NATIONAL_REGISTRY" || channel?.source === "BOTH")) parts.push("National Registry");
  return parts.length ? parts.join(" + ") : null;
}

export function channelBasis(channel: DncChannelResult | undefined): string | null {
  if (!channel || channel.status !== "SUPPRESSED") return null;
  const parts: string[] = [];
  if (channel.source === "INTERNAL" || channel.source === "BOTH") parts.push(channel.scope === "ASSOCIATE" ? "Associate-specific" : "KWSA-wide");
  if (channel.source === "NATIONAL_REGISTRY" || channel.source === "BOTH") parts.push("National Registry");
  return parts.join(" + ");
}

export function channelsLabel(channels: string[] | null | undefined): string {
  if (!channels?.length) return "Not recorded";
  if (channels.includes("ALL_MARKETING")) return "All marketing channels";
  return dncChannels.filter(({ value }) => channels.includes(value)).map(({ label }) => label).join(", ") || channels.join(", ");
}

export function sourceTypeLabel(sourceType: string | null | undefined): string {
  if (!sourceType) return "Not recorded";
  return sourceTypeLabels[sourceType] ?? sourceType;
}

export function recordStatusLabel(action: string): { label: string; tone: "danger" | "neutral" | "warning" } {
  if (action === "ADDED") return { label: "Opted out", tone: "danger" };
  if (action === "REMOVED") return { label: "Withdrawn", tone: "neutral" };
  return { label: "Review", tone: "warning" };
}

export function consumerName(first: string | null | undefined, last: string | null | undefined): string {
  return [first, last].filter(Boolean).join(" ") || "Name not recorded";
}

export function formatDncDate(value: string | null | undefined): string {
  if (!value) return "Not recorded";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("en-ZA", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export const nationalRegistryWorkflowSteps = [
  "Prepare",
  "Generate",
  "Download",
  "Submit",
  "Record Reference",
  "Upload Return",
  "Validate",
  "Preview",
  "Process",
  "Complete",
];
